import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import '@workpay/ds-shared/utils/pdfWorkerSetup';
import { dsEnvelopeService, getReviewPrefill } from '@services/dsEnvelopeService';
import { dsFileService } from '@services/dsFileService';
import { dsReviewService } from '@services/dsReviewService';
import SendForReviewModal from '@features/document-reviews/SendForReviewModal';
import { ReviewStatusPanel } from '@workpay/ds-shared/components/ReviewStatusPanel';
import { ChatWidget } from '@workpay/ds-shared/components/ChatWidget';
import { AuditTrailView } from '@workpay/ds-shared/components/AuditTrailView';
import { OnlyOfficeEditor } from '@workpay/ds-shared/components/OnlyOfficeEditor';
import { PdfReviewViewer } from '@workpay/ds-shared/components/PdfReviewViewer';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import { useTranslation } from '@hooks/useTranslation';
import { useNotificationStore } from '@stores/notificationStore';
import { useReviewSse } from '@workpay/ds-shared/hooks/useReviewSse';
import { toTitleCase } from '@utils/textCase';
import { getReviewerBadgeClass } from '@utils/reviewerStatus';
import type { EnvelopeDocument } from '@workpay/ds-shared/types/envelope';
import { RecipientStatusList } from './RecipientStatusList';
import { SigningRequestActions } from './SigningRequestActions';
import { AuditTrailModal } from '@components/shared/AuditTrailModal';
import { AUDIT_RESOURCE } from '@lib/auditResourceTypes';
import styles from './SigningRequestDetail.module.scss';

interface SigningRequestDetailProps {
  requestId: string;
  routeTo: string;
}

function isDocxDoc(doc: EnvelopeDocument | undefined): boolean {
  if (!doc) return false;
  return (
    doc.sourceContentType?.includes('wordprocessingml') === true ||
    doc.name?.toLowerCase().endsWith('.docx') === true
  );
}

export function SigningRequestDetail({ requestId, routeTo }: SigningRequestDetailProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [activeDocOrder, setActiveDocOrder] = useState(1);
  const [splitPercent, setSplitPercent] = useState(40);
  const [auditOpen, setAuditOpen] = useState(false);
  const splitContainerRef = useRef<HTMLDivElement>(null);

  const {
    data: signingRequest,
    isLoading,
    dataUpdatedAt,
    isRefetching,
    refetch,
    error: signingRequestErrorObj,
  } = useQuery({
    queryKey: ['dsEnvelope', requestId],
    queryFn: () => dsEnvelopeService.getById(requestId),
    refetchInterval: 15_000,
    refetchOnWindowFocus: 'always',
    staleTime: 10_000,
  });
  const signingRequestForbidden = isForbiddenError(signingRequestErrorObj);

  // Backend-composed Send-for-Review prefill — target employee + union of
  // default reviewers across every source template on this envelope, deduped
  // on lower(email). Envelope-id driven (rewritten 2026-04-28).
  const { data: reviewPrefill } = useQuery({
    queryKey: ['envelopeReviewPrefill', requestId],
    queryFn: () => getReviewPrefill(requestId),
    enabled: !!requestId,
  });

  const initialReviewers = useMemo(() => {
    if (!reviewPrefill) return undefined;
    const seen = new Set<string>();
    const deduped: Array<{ name: string; email: string }> = [];
    for (const r of reviewPrefill.reviewers) {
      if (!r.email) continue;
      const key = r.email.trim().toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push({ name: r.name ?? r.email, email: r.email });
    }
    return deduped.length > 0 ? deduped : undefined;
  }, [reviewPrefill]);

  const documents = signingRequest?.documents ?? [];
  const activeDoc = documents.find((d) => d.documentOrder === activeDocOrder) ?? documents[0];
  const isActiveDocDocx = isDocxDoc(activeDoc);
  const activeFileId =
    activeDoc?.signedFileId ?? activeDoc?.intermediateFileId ?? activeDoc?.sourceFileId;

  const isInReview =
    signingRequest?.status === 'PENDING_REVIEW' || signingRequest?.status === 'UNDER_NEGOTIATION';
  // OnlyOffice only applies while the author is pre-send (DRAFT or the
  // post-review parking state REVIEW_COMPLETED) or the envelope is under
  // review. Post-send states (SENT → COMPLETED, plus DECLINED/VOIDED/EXPIRED)
  // render a PDF — once signing starts, the source DOCX is stamped into a
  // signed PDF (signedFileId) and the DOCX side is no longer authoritative.
  // REVIEW_COMPLETED is DRAFT-equivalent for write ops, so the editor opens
  // in plain view/edit mode (not review-author).
  const isDraftLike =
    signingRequest?.status === 'DRAFT' || signingRequest?.status === 'REVIEW_COMPLETED';
  const isDraftOrInReview = isDraftLike || isInReview;
  const showOnlyOffice = isActiveDocDocx && isDraftOrInReview && !!activeFileId;

  // PDF blob: native PDFs download directly; DOCX-source docs need convert-to-pdf
  // ONLY when we've fallen back to the raw sourceFileId. At sendEnvelope, the
  // backend already converts every DOCX to a PDF and stores it on
  // intermediateFileId (EnvelopeServiceImpl.convertDocxDocumentsToPdf). signedFileId
  // (post-completion) is likewise always a PDF. So post-send flows — including
  // DECLINED/VOIDED/EXPIRED — resolve to a PDF and can be downloaded directly;
  // calling convert-to-pdf on those would waste a round-trip or 400. Only the
  // DRAFT/pre-send DOCX-source edge case (no intermediate, no signed) needs
  // the conversion endpoint.
  const needsPdfConversion =
    isActiveDocDocx && !activeDoc?.signedFileId && !activeDoc?.intermediateFileId;

  // Multi-doc blob URLs cached within a single page visit, not across
  // mount/remount. Each (docOrder, fileId, mode) tuple has its own cache entry
  // so tab-switches within the page don't re-fetch. `gcTime: 0` dumps every
  // entry the moment the component unmounts, which lines up with our
  // cleanup-on-unmount revoke of the URL set — so navigating back to the list
  // and then back into the detail page does a clean fresh fetch instead of
  // serving a cached-but-revoked blob URL and 404'ing in react-pdf.
  const createdBlobUrlsRef = useRef<Set<string>>(new Set());
  const pdfBlobQuery = useQuery({
    queryKey: [
      'signingRequestPdfBlob',
      requestId,
      activeDocOrder,
      activeFileId,
      needsPdfConversion ? 'convert' : 'download',
    ],
    queryFn: async () => {
      if (!activeFileId) return null;
      const res = needsPdfConversion
        ? await dsFileService.convertToPdf(activeFileId)
        : await dsFileService.download(activeFileId);
      if (!res.data || res.data.size === 0) return null;
      const url = URL.createObjectURL(res.data);
      createdBlobUrlsRef.current.add(url);
      return url;
    },
    enabled: !!activeFileId && !isLoading && !showOnlyOffice,
    staleTime: Infinity,
    gcTime: 0,
  });
  const pdfUrl = pdfBlobQuery.data ?? undefined;
  const pdfLoading = pdfBlobQuery.isLoading || pdfBlobQuery.isFetching;

  useEffect(() => {
    const created = createdBlobUrlsRef.current;
    return () => {
      created.forEach((u) => URL.revokeObjectURL(u));
      created.clear();
    };
  }, []);

  // Reviews for ChatSidebar
  const { data: reviews = [] } = useQuery({
    queryKey: ['ds-reviews', 'ENVELOPE', requestId],
    queryFn: () => dsReviewService.getForTarget('ENVELOPE', requestId),
    refetchInterval: 10_000,
  });

  useReviewSse({ mode: 'author', targetType: 'ENVELOPE', targetId: requestId });

  // Draggable split handle
  const handleSplitStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const container = splitContainerRef.current;
    if (!container) return;
    const onMouseMove = (ev: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setSplitPercent(Math.max(25, Math.min(75, pct)));
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const getStatusBadgeClass = useCallback((status: string) => {
    switch (status) {
      case 'COMPLETED':
      case 'REVIEW_COMPLETED':
        return styles.badgeSuccess;
      case 'DRAFT':
        return styles.badgeWarning;
      case 'SENT':
      case 'VIEWED':
      case 'SIGNING_IN_PROGRESS':
      case 'DELIVERED':
      case 'PENDING_REVIEW':
      case 'UNDER_NEGOTIATION':
        return styles.badgeInfo;
      case 'VOIDED':
      case 'DECLINED':
      case 'EXPIRED':
        return styles.badgeNeutral;
      default:
        return styles.badgeNeutral;
    }
  }, []);

  // --- Forbidden (page-level — detail route can't render anything without the signing request) ---
  if (signingRequestForbidden) {
    return (
      <div className={styles.page}>
        <Forbidden
          variant="page"
          onAction={() => navigate({ to: routeTo })}
          actionLabel={t('K_BUTTON_GO_BACK', 'Go Back')}
        />
      </div>
    );
  }

  if (isLoading || !signingRequest) {
    return (
      <div className={styles.loading}>
        {t('K_DS_LOADING_SIGNING_REQUEST', 'Loading signing request...')}
      </div>
    );
  }

  // PDF review mode — signing request under review AND doc is a PDF AND review has a version chain.
  // When any of those fail we fall back to the plain read-only PdfPreview below.
  const activeReview = reviews.find((r) => r.status === 'IN_PROGRESS') ?? reviews[0];
  const reviewDoc = activeReview?.documents?.find((rd) => rd.envelopeDocumentId === activeDoc?.id);
  const reviewVersions = reviewDoc?.versions ?? activeReview?.versions ?? [];
  const showPdfReviewViewer =
    isInReview &&
    !isActiveDocDocx &&
    !!activeReview?.id &&
    reviewVersions.length > 0;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.backBtn} onClick={() => navigate({ to: routeTo })}>
            &larr; {t('K_DS_SIGNING_REQUESTS', 'Signing Requests')}
          </button>
          <h1 className={styles.title} title={signingRequest.subject}>
            {signingRequest.subject}
          </h1>
          <span className={`${styles.badge} ${getStatusBadgeClass(signingRequest.status)}`}>
            {toTitleCase(signingRequest.status.replace(/_/g, ' '))}
          </span>
          <span className={`${styles.badge} ${styles.badgeNeutral}`}>
            {toTitleCase(signingRequest.signingMethod.replace(/_/g, ' '))}
          </span>
        </div>
        <div className={styles.headerActions}>
          {isInReview && (
            <ReviewStatusPanel
              targetType="ENVELOPE"
              targetId={requestId}
              compact
              hideActions
              onStatusChange={() => {
                void queryClient.invalidateQueries({ queryKey: ['dsEnvelope', requestId] });
              }}
            />
          )}
          {dataUpdatedAt > 0 && (
            <span className={styles.lastUpdated}>
              {t('K_DS_LAST_UPDATED', 'Updated')}{' '}
              <TimeAgo timestamp={dataUpdatedAt} />
            </span>
          )}
          <button
            className={styles.refreshBtn}
            onClick={() => refetch()}
            disabled={isRefetching}
            title={t('K_DS_REFRESH_SIGNING_REQUEST', 'Refresh signing request data')}
            aria-label={t('K_DS_REFRESH', 'Refresh')}
          >
            <svg
              className={`${styles.refreshIcon} ${isRefetching ? styles.refreshIconSpin : ''}`}
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M1 1v5h5" />
              <path d="M15 15v-5h-5" />
              <path d="M13.5 6A6 6 0 0 0 3 3.5L1 6" />
              <path d="M2.5 10A6 6 0 0 0 13 12.5L15 10" />
            </svg>
          </button>
          <SigningRequestActions
            signingRequest={signingRequest}
            onSendForReview={() => setShowReviewModal(true)}
            onAudit={() => setAuditOpen(true)}
          />
        </div>
      </div>

      <div className={styles.content} ref={splitContainerRef}>
        <div className={styles.leftPanel} style={{ width: `${splitPercent}%` }}>
          <div className={styles.leftPanelScroll}>
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('K_DS_DETAILS', 'Details')}</h2>
              <div className={styles.detailGrid}>
                {signingRequest.message && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>
                      {t('K_DS_LABEL_MESSAGE', 'Message')}
                    </span>
                    <span className={styles.detailValue}>{signingRequest.message}</span>
                  </div>
                )}
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>
                    {t('K_DS_COLUMN_SENDER', 'Sender')}
                  </span>
                  <span className={styles.detailValue}>
                    {signingRequest.senderName} ({signingRequest.senderEmail})
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>
                    {t('K_COLUMN_CREATED', 'Created')}
                  </span>
                  <span className={styles.detailValue}>
                    {new Date(signingRequest.createdOn).toLocaleString()}
                  </span>
                </div>
                {signingRequest.expiresAt && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>
                      {t('K_DS_LABEL_EXPIRES_AT', 'Expires At')}
                    </span>
                    <span className={styles.detailValue}>
                      {new Date(signingRequest.expiresAt).toLocaleString()}
                    </span>
                  </div>
                )}
                {signingRequest.completedAt && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>
                      {t('K_DS_LABEL_COMPLETED_AT', 'Completed At')}
                    </span>
                    <span className={styles.detailValue}>
                      {new Date(signingRequest.completedAt).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>
                {t('K_DS_RECIPIENTS', 'Recipients')} ({signingRequest.recipients?.length ?? 0})
              </h2>
              <RecipientStatusList
                recipients={signingRequest.recipients ?? []}
                requestId={requestId}
                requestStatus={signingRequest.status}
              />
            </section>

            {((signingRequest.cc?.length ?? 0) > 0 || (signingRequest.bcc?.length ?? 0) > 0) && (
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>
                  {t('K_DS_SECTION_OTHER_RECIPIENTS_TITLE', 'Other Recipients')}
                </h2>
                <div className={styles.detailGrid}>
                  {(signingRequest.cc?.length ?? 0) > 0 && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t('K_DS_LABEL_CC', 'CC')}</span>
                      <span className={styles.detailValue}>
                        {signingRequest.cc!.join(', ')}
                      </span>
                    </div>
                  )}
                  {(signingRequest.bcc?.length ?? 0) > 0 && (
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t('K_DS_LABEL_BCC', 'BCC')}</span>
                      <span className={styles.detailValue}>
                        {signingRequest.bcc!.join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              </section>
            )}

            {reviews.some((r) => (r.reviewers?.length ?? 0) > 0) && (() => {
              const withReviewers = reviews
                .filter((r) => (r.reviewers?.length ?? 0) > 0)
                .slice()
                .reverse();
              const showCycleLabels = withReviewers.length > 1;
              return (
                <section className={styles.section}>
                  <h2 className={styles.sectionTitle}>{t('K_DS_REVIEWERS', 'Reviewers')}</h2>
                  {withReviewers.map((review, idx) => (
                    <div key={review.id}>
                      {showCycleLabels && (
                        <div className={styles.reviewCycleLabel}>
                          {review.status === 'IN_PROGRESS'
                            ? `${t('K_DS_REVIEW', 'Review')} ${idx + 1} (${t('K_DS_CURRENT', 'Current')})`
                            : `${t('K_DS_REVIEW', 'Review')} ${idx + 1}`}
                          {review.status !== 'IN_PROGRESS' && (
                            <span
                              className={`${styles.badge} ${review.status === 'COMPLETED' ? styles.badgeSuccess : styles.badgeNeutral}`}
                            >
                              {toTitleCase(review.status)}
                            </span>
                          )}
                        </div>
                      )}
                      {(review.reviewers ?? []).map((r) => (
                        <div key={r.id} className={styles.reviewerCard}>
                          <div className={styles.reviewerInfo}>
                            <span className={styles.reviewerName} title={r.name}>
                              {r.name}
                            </span>
                            <span className={styles.reviewerEmail} title={r.email}>
                              {r.email}
                            </span>
                          </div>
                          <span className={`${styles.badge} ${getReviewerBadgeClass(r.status, styles)}`}>
                            {toTitleCase(r.status)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </section>
              );
            })()}

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>
                {t('K_DS_SECTION_SIGNING_ACTIVITY', 'Signing activity')}
              </h2>
              <AuditTrailView envelopeId={requestId} />
            </section>

          </div>
        </div>

        <div
          className={styles.splitHandle}
          onMouseDown={handleSplitStart}
          role="separator"
          aria-orientation="vertical"
          aria-label={t('K_DS_RESIZE_PANEL', 'Resize panel')}
        />

        <div className={styles.rightPanel}>
          {documents.length > 1 && (
            <div className={styles.docTabs}>
              {documents.map((doc) => {
                const isActive = doc.documentOrder === activeDocOrder;
                const isDocx = isDocxDoc(doc);
                return (
                  <button
                    key={doc.id ?? doc.documentOrder}
                    className={`${styles.docTab} ${isActive ? styles.docTabActive : ''}`}
                    onClick={() => setActiveDocOrder(doc.documentOrder)}
                    title={doc.name}
                  >
                    <span className={styles.docTabOrder}>{doc.documentOrder}</span>
                    <span className={styles.docTabName}>{doc.name}</span>
                    <span className={`${styles.docTabFormat} ${isDocx ? styles.docTabFormatDocx : styles.docTabFormatPdf}`}>
                      {isDocx ? 'DOCX' : 'PDF'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          <div className={styles.previewToolbar}>
            <span className={styles.previewToolbarTitle}>
              {activeDoc?.name ?? signingRequest.subject}
            </span>
            {activeDoc && (
              <span className={styles.previewToolbarMeta}>
                {activeDoc.pageCount}{' '}
                {activeDoc.pageCount === 1 ? t('K_DS_PAGE', 'page') : t('K_DS_PAGES', 'pages')}
              </span>
            )}
          </div>
          <div className={styles.previewContent}>
            {showPdfReviewViewer ? (
              <PdfReviewViewer
                key={`pdf-review-${activeDocOrder}-${activeReview!.id}`}
                mode="author"
                reviewId={activeReview!.id}
                reviewDocumentId={reviewDoc?.id}
                versions={reviewVersions}
                resolveFileUrl={(id) => dsFileService.getFileUrl(id)}
                onFileReplaced={() => {
                  void queryClient.invalidateQueries({
                    queryKey: ['ds-reviews', 'ENVELOPE', requestId],
                  });
                  void queryClient.invalidateQueries({ queryKey: ['dsEnvelope', requestId] });
                }}
              />
            ) : showOnlyOffice ? (
              <OnlyOfficeEditor
                key={`ooxml-${activeDocOrder}-${activeFileId}-${isInReview ? 'review-author' : 'view'}`}
                fileId={activeFileId!}
                mode={isInReview ? 'review-author' : 'view'}
                flow="ENVELOPE"
                onSave={() => {
                  void queryClient.invalidateQueries({
                    queryKey: ['ds-reviews', 'ENVELOPE', requestId],
                  });
                  void queryClient.invalidateQueries({ queryKey: ['dsEnvelope', requestId] });
                }}
                onError={(err) => {
                  addNotification({
                    variant: 'critical',
                    title: t('K_DS_ERROR_EDITOR_LOAD', 'Failed to load editor'),
                    description: err,
                  });
                }}
              />
            ) : pdfLoading ? (
              <div className={styles.pdfLoader}>
                <div className={styles.spinner} />
                {t('K_DS_LOADING_PREVIEW', 'Loading preview...')}
              </div>
            ) : pdfUrl ? (
              <div className={styles.pdfViewerWrap}>
                <PdfPreview url={pdfUrl} />
              </div>
            ) : (
              <div className={styles.pdfLoader}>
                {t('K_DS_NO_PREVIEW', 'No preview available')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Floating chat widget — same surface templates use (see
          TemplateDetail.tsx:541). Replaces the inline "Review Conversation"
          block so authors can reference the chat while the document is in
          view. */}
      {reviews.length > 0 && (
        <ChatWidget
          targetType="ENVELOPE"
          targetId={requestId}
          reviews={reviews}
          mode="author"
          positioning="absolute"
        />
      )}

      {showReviewModal && (
        <SendForReviewModal
          targetType="ENVELOPE"
          targetId={requestId}
          initialReviewers={initialReviewers}
          onClose={() => setShowReviewModal(false)}
          onSuccess={() => {
            setShowReviewModal(false);
            queryClient.invalidateQueries({ queryKey: ['dsEnvelope', requestId] });
          }}
        />
      )}

      <AuditTrailModal
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        filter={{ resourceType: AUDIT_RESOURCE.SIGNING_REQUEST, resourceId: requestId }}
        subject={signingRequest.subject || undefined}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function PdfPreview({ url }: { url: string }) {
  const [pages, setPages] = useState(0);
  return (
    <Document
      file={url}
      onLoadSuccess={({ numPages }) => setPages(numPages)}
      loading={null}
      error={null}
    >
      {Array.from({ length: pages }, (_, i) => (
        <Page
          key={`pdf-page-${i + 1}`}
          pageNumber={i + 1}
          width={700}
          renderTextLayer={false}
          renderAnnotationLayer={false}
        />
      ))}
    </Document>
  );
}

// TimeAgo — small presentational helper for the "Updated Xs ago" pill.
function TimeAgo({ timestamp }: { timestamp: number }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 15_000);
    return () => clearInterval(interval);
  }, []);
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 10) return <>just now</>;
  if (seconds < 60) return <>{seconds}s ago</>;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return <>{minutes}m ago</>;
  const hours = Math.floor(minutes / 60);
  return <>{hours}h ago</>;
}
